import { useEffect, useState } from 'react';
import { api, resolveUrl } from '../api.ts';
import type { HunterApproval, HunterApprovalRequest } from '../api.ts';
import { getTheme, useThemeMode } from '../theme';

/** Jägarens egen status: krävs för att köpa jakt. Visas under jägarexamensbeviset i profilen. */
export function HunterApprovalStatus({ hasLocalExam }: { hasLocalExam: boolean }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [approval, setApproval] = useState<HunterApproval | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { api.hunterApproval().then(setApproval).catch(() => {}); }, [hasLocalExam]);

  async function request() {
    setBusy(true); setErr('');
    try { setApproval(await api.requestHunterApproval()); }
    catch (e: any) { setErr(e.message); }
    setBusy(false);
  }

  if (!approval) return null;
  const box = (bg: string, border: string) => ({ background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: 12, fontSize: 13, color: T.text, lineHeight: 1.6, marginTop: 16 });

  if (approval.status === 'approved') {
    return <div style={box('rgba(97,216,54,0.08)', 'rgba(97,216,54,0.35)')}><strong style={{ color: '#61D836' }}>✓ Godkänd jägare</strong> — du kan köpa jakter.</div>;
  }
  if (approval.status === 'pending') {
    return <div style={box('rgba(238,143,9,0.08)', T.amberDim)}><strong>Granskas</strong> — vi går igenom ditt jägarexamensbevis. Du kan köpa jakter när det är godkänt.</div>;
  }
  return (
    <div style={box(T.surface, T.border)}>
      {approval.status === 'rejected' && (
        <div style={{ marginBottom: 8 }}><strong style={{ color: '#f88' }}>Inte godkänd.</strong>{approval.note ? ` ${approval.note}` : ''} Ladda upp ett nytt bevis och ansök igen.</div>
      )}
      <div style={{ color: T.textSub, marginBottom: 10 }}>För att köpa jakter måste du vara godkänd jägare. En administratör granskar ditt jägarexamensbevis.</div>
      {!hasLocalExam && <div style={{ color: T.textMuted, fontSize: 12, marginBottom: 10 }}>Ladda upp ditt bevis ovan och spara profilen först.</div>}
      <button type="button" onClick={request} disabled={busy || !hasLocalExam}
        style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 5, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: busy || !hasLocalExam ? 0.5 : 1 }}>
        {busy ? 'Skickar…' : 'Ansök om att bli godkänd jägare'}
      </button>
      {err && <div style={{ color: '#f88', fontSize: 12, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

/** Admin: granska jägarexamensbevis och godkänn/avslå. */
export function HunterApprovalAdmin() {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [rows, setRows] = useState<HunterApprovalRequest[] | null>(null);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    setRows(null); setErr('');
    api.adminHunterApprovals(status).then(setRows).catch(e => { setErr(e.message); setRows([]); });
  }
  useEffect(load, [status]);

  async function decide(r: HunterApprovalRequest, decision: 'approved' | 'rejected') {
    let note: string | undefined;
    if (decision === 'rejected') {
      const n = prompt('Motivering till jägaren (valfri):');
      if (n === null) return;
      note = n;
    }
    setBusyId(r.userId);
    try { await api.decideHunterApproval(r.userId, decision, note); load(); }
    catch (e: any) { alert(e.message); }
    setBusyId(null);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {(['pending', 'approved', 'rejected'] as const).map(s => (
          <button key={s} type="button" onClick={() => setStatus(s)} style={{ padding: '4px 12px', borderRadius: 6, border: `1px solid ${status === s ? T.amber : T.border}`, background: status === s ? 'rgba(238,143,9,0.15)' : 'transparent', color: status === s ? T.amber : T.textSub, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {s === 'pending' ? 'Att granska' : s === 'approved' ? 'Godkända' : 'Avslagna'}
          </button>
        ))}
      </div>
      {err && <div style={{ color: '#f88', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {!rows && <div style={{ color: T.textMuted, fontSize: 13 }}>Laddar…</div>}
      {rows && rows.length === 0 && !err && <div style={{ color: T.textMuted, fontSize: 13 }}>Inga jägare här.</div>}
      <div style={{ display: 'grid', gap: 12 }}>
        {rows?.map(r => (
          <div key={r.userId} style={{ display: 'flex', gap: 14, flexWrap: 'wrap', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 12 }}>
            {r.hunterExam?.imageUrl && (
              <a href={resolveUrl(r.hunterExam.imageUrl)} target="_blank" rel="noopener noreferrer">
                <img src={resolveUrl(r.hunterExam.imageUrl)} alt="Jägarexamensbevis" style={{ width: 160, height: 110, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
              </a>
            )}
            <div style={{ flex: '1 1 200px', minWidth: 0, fontSize: 13, color: T.text, lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700 }}>{r.name || r.email || r.userId}</div>
              {r.email && <div style={{ color: T.textSub, overflowWrap: 'anywhere' }}>{r.email}</div>}
              {r.hunterExam && (
                <div style={{ color: T.textSub, fontSize: 12 }}>
                  {r.hunterExam.holderName || '—'} · examen {r.hunterExam.examDate || '—'} · nr {r.hunterExam.examNumber || '—'}
                </div>
              )}
              {r.note && <div style={{ color: T.textMuted, fontSize: 12 }}>Motivering: {r.note}</div>}
              {r.approvedBy && <div style={{ color: T.textMuted, fontSize: 12 }}>Beslut av {r.approvedBy}</div>}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              {r.status !== 'approved' && (
                <button type="button" disabled={busyId === r.userId} onClick={() => decide(r, 'approved')} style={{ background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Godkänn</button>
              )}
              {r.status !== 'rejected' && (
                <button type="button" disabled={busyId === r.userId} onClick={() => decide(r, 'rejected')} style={{ background: 'rgba(255,80,80,0.15)', color: '#f88', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 4, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Avslå</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
